import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, Ban, RefreshCw } from "lucide-react";
import { formatDateTime } from "@/lib/utils";

export default function Verify() {
  const { certNumber } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/public/verify/${encodeURIComponent(certNumber)}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [certNumber]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="max-w-lg w-full p-8 border border-border" data-testid="verify-card">
        <img src="/brand/shakun-logo.svg" alt="Shakun Polymers"
             className="h-12 w-auto mb-5 dark:hidden" width="202" height="48" />
        <img src="/brand/shakun-logo-light.svg" alt="Shakun Polymers"
             className="h-12 w-auto mb-5 hidden dark:block" width="202" height="48" />
        <div className="overline text-primary mb-2">Certificate Verification</div>
        {loading ? (
          <p className="mt-6 text-muted-foreground">Checking...</p>
        ) : data?.valid ? (
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-2 text-emerald-600 font-medium"><CheckCircle2 className="w-5 h-5" /> Valid certificate</div>
            <div className="grid grid-cols-2 gap-4 text-sm mt-4 border-t border-border pt-4">
              <div><div className="overline text-muted-foreground">Certificate #</div><div className="font-mono mt-1">{data.certificate_number}</div></div>
              <div><div className="overline text-muted-foreground">Issued to</div><div className="font-medium mt-1">{data.full_name}</div></div>
              <div><div className="overline text-muted-foreground">Employee code</div><div className="mt-1">{data.employee_code}</div></div>
              <div><div className="overline text-muted-foreground">Department</div><div className="mt-1">{data.department_name}</div></div>
              {/* Two units, and this page showed one of them labelled "Unit".
                  The person's unit sits beside their department because both describe
                  them; where the session was HELD is a different fact, and it is the one
                  a certificate can print via {training_unit} — which Settings invites.
                  Unlabelled, the paper and this page could name different plants for the
                  same certificate, with nothing saying which meaning either was. That is
                  the failure the certificates register was already fixed for; this page
                  never got the same treatment. */}
              <div><div className="overline text-muted-foreground">Employee's unit</div><div className="mt-1">{data.unit_name || "—"}</div></div>
              <div><div className="overline text-muted-foreground">Training held at</div><div className="mt-1">{data.training_unit_name || "—"}</div></div>
              <div className="col-span-2"><div className="overline text-muted-foreground">Training</div><div className="font-medium mt-1">{data.title}</div></div>
              <div><div className="overline text-muted-foreground">Category</div><div className="mt-1">{data.category}</div></div>
              <div><div className="overline text-muted-foreground">Date</div><div className="mt-1">{data.training_date}</div></div>
            </div>
          </div>
        ) : data?.replaced ? (
          // Replaced is not revoked, and this page used to say revoked for both.
          // "Withdrawn" invites a question about the person holding the paper;
          // "we corrected it and here is the new number" does not. The replacement is
          // named so an auditor scanning old paper reaches the current certificate in
          // one step rather than by asking HR.
          <div className="mt-6 space-y-3" data-testid="verify-replaced">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500 font-medium">
              <RefreshCw className="w-5 h-5" /> This certificate has been replaced
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm mt-4 border-t border-border pt-4">
              <div><div className="overline text-muted-foreground">Certificate #</div><div className="font-mono mt-1">{data.certificate_number}</div></div>
              <div>
                <div className="overline text-muted-foreground">Replaced by</div>
                {/* A link, not a string to retype. Naming the replacement was meant to be
                    one step from the old paper to the current certificate; printing it
                    unlinked left an auditor copying "SPPL/TRN/2026/0018" by hand off a
                    phone. The number contains slashes and the route is one segment. */}
                <div className="font-mono mt-1">
                  {data.replaced_by ? (
                    <a href={`/verify/${encodeURIComponent(data.replaced_by)}`}
                       className="text-primary hover:underline" data-testid="verify-replacement-link">
                      {data.replaced_by}
                    </a>
                  ) : "—"}
                </div>
              </div>
              <div><div className="overline text-muted-foreground">Replaced on</div><div className="mt-1">{formatDateTime(data.replaced_at)}</div></div>
            </div>
            <p className="text-sm text-muted-foreground pt-2">
              Shakun Polymers issued a corrected certificate in its place. This number is
              no longer the current one and is never reused — follow the replacement above
              to the certificate that stands.
            </p>
          </div>
        ) : data?.revoked ? (
          // Revoked is not the same answer as unknown. Somebody holding the printout is
          // owed the difference: this was issued and later withdrawn. Why it was
          // withdrawn stays internal — this page is public.
          <div className="mt-6 space-y-3" data-testid="verify-revoked">
            <div className="flex items-center gap-2 text-destructive-text font-medium"><Ban className="w-5 h-5" /> This certificate has been revoked</div>
            <div className="grid grid-cols-2 gap-4 text-sm mt-4 border-t border-border pt-4">
              <div><div className="overline text-muted-foreground">Certificate #</div><div className="font-mono mt-1">{data.certificate_number}</div></div>
              <div><div className="overline text-muted-foreground">Revoked on</div><div className="mt-1">{formatDateTime(data.revoked_at)}</div></div>
            </div>
            <p className="text-sm text-muted-foreground pt-2">
              It was issued by Shakun Polymers and later withdrawn, and is no longer valid.
              Contact HR if you believe this is a mistake.
            </p>
          </div>
        ) : (
          <div className="mt-6 flex items-center gap-2 text-destructive-text font-medium" data-testid="verify-invalid"><XCircle className="w-5 h-5" /> Certificate not found or invalid</div>
        )}
      </Card>
    </div>
  );
}
